import React, { useState } from 'react';
import { Box, Paper, Tabs, Title, Text, Button, Textarea, ActionIcon, Tooltip, Group } from '@mantine/core';
import { IconCopy, IconX } from '@tabler/icons-react';
import { ScimMonacoEditor } from '../common/ScimMonacoEditor';
import { QuillEditor } from '../common/QuillEditor';
import { HtmlPreview } from '../common/HtmlPreview';
import { extractBodyContent } from '../../utils/htmlFormatter';
import { copyToClipboard } from '../../utils/clipboardUtils';

const FreeEdit: React.FC = () => {
    const [activeTab, setActiveTab] = useState<string | null>('monaco');
    const [monacoTab, setMonacoTab] = useState<string | null>('split');
    const [htmlContent, setHtmlContent] = useState<string>('');
    const [extractedBody, setExtractedBody] = useState<string>('');

    const handleHtmlChange = (newValue: string) => {
        setHtmlContent(newValue);
        const body = extractBodyContent(newValue);
        setExtractedBody(body);
    };

    const handleQuillChange = (newValue: string) => {
        setExtractedBody(newValue);
        setHtmlContent(`<html><head></head><body>${newValue}</body></html>`);
    };

    const handleExtractBody = () => {
        const body = extractBodyContent(htmlContent);
        setExtractedBody(body);
    };

    const handleClear = () => {
        setHtmlContent('');
        setExtractedBody('');
    };

    const handleCopyBody = () => {
        copyToClipboard(extractedBody);
    };

    return (
        <Box p="lg">
            <Title order={4} mb="xs">
                Free Edit
            </Title>
            <Text size="sm" c="dimmed" mb="lg">
                Play with Monaco and Quill editors. Edit HTML and extract the body content.
            </Text>

            <Paper withBorder mb="md">
                <Group justify="space-between" align="center" p="md" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                    <Tabs value={activeTab} onChange={setActiveTab}>
                        <Tabs.List>
                            <Tabs.Tab value="monaco">Monaco Editor</Tabs.Tab>
                            <Tabs.Tab value="quill">Quill Editor</Tabs.Tab>
                        </Tabs.List>
                    </Tabs>
                    <Group gap="xs">
                        <Button
                            variant="outline"
                            size="xs"
                            leftSection={<IconX size={16} />}
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                        {activeTab === 'monaco' && (
                            <Button
                                variant="outline"
                                size="xs"
                                onClick={handleExtractBody}
                            >
                                Extract Body
                            </Button>
                        )}
                    </Group>
                </Group>

                <Box style={{ position: 'relative' }}>
                    {activeTab === 'monaco' && (
                        <Box>
                            <Tabs value={monacoTab} onChange={setMonacoTab}>
                                <Tabs.List style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                                    <Tabs.Tab value="split">Split View</Tabs.Tab>
                                    <Tabs.Tab value="editor">Editor</Tabs.Tab>
                                    <Tabs.Tab value="preview">Preview</Tabs.Tab>
                                </Tabs.List>
                                <Box style={{ position: 'relative' }}>
                                    {monacoTab === 'split' && (
                                        <Box style={{ display: 'flex', height: 500 }}>
                                            <Box flex={1} style={{ borderRight: '1px solid var(--mantine-color-default-border)' }}>
                                                <ScimMonacoEditor
                                                    language="html"
                                                    value={htmlContent}
                                                    onChange={(newValue) => handleHtmlChange(newValue || '')}
                                                    height="500px"
                                                    fontSize={14}
                                                    showLineNumbers={true}
                                                    showMinimap={false}
                                                    wordWrap="on"
                                                    readOnly={false}
                                                />
                                            </Box>
                                            <Box flex={1} style={{ overflow: 'hidden' }}>
                                                <HtmlPreview
                                                    content={htmlContent}
                                                    emptyMessage="Start typing HTML to see preview..."
                                                    height="100%"
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                    {monacoTab === 'editor' && (
                                        <ScimMonacoEditor
                                            language="html"
                                            value={htmlContent}
                                            onChange={(newValue) => handleHtmlChange(newValue || '')}
                                            height="500px"
                                            fontSize={14}
                                            showLineNumbers={true}
                                            showMinimap={false}
                                            wordWrap="on"
                                            readOnly={false}
                                        />
                                    )}
                                    {monacoTab === 'preview' && (
                                        <HtmlPreview
                                            content={htmlContent}
                                            emptyMessage="No content to preview"
                                            height="500px"
                                        />
                                    )}
                                </Box>
                            </Tabs>
                        </Box>
                    )}

                    {activeTab === 'quill' && (
                        <Box p="md">
                            <QuillEditor
                                value={extractedBody}
                                onChange={handleQuillChange}
                                height="500px"
                                disabled={false}
                                placeholder="Start typing HTML content..."
                                name="free-edit-quill"
                            />
                        </Box>
                    )}
                </Box>
            </Paper>

            <Paper withBorder>
                <Group justify="space-between" align="center" p="md" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                    <Title order={6}>Extracted Body Content</Title>
                    <Tooltip label="Copy to clipboard">
                        <ActionIcon
                            size="sm"
                            variant="subtle"
                            onClick={handleCopyBody}
                            disabled={!extractedBody}
                        >
                            <IconCopy size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
                <Box p="md">
                    <Textarea
                        value={extractedBody}
                        onChange={(e) => setExtractedBody(e.currentTarget.value)}
                        placeholder="Body content will appear here..."
                        minRows={10}
                        styles={{
                            input: {
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                            },
                        }}
                    />
                </Box>
            </Paper>
        </Box>
    );
};

export default FreeEdit;
